import { useSyncExternalStore } from 'react';

export type BetaThemeName = 'light' | 'dark';

const STORAGE_KEY = 'pokebinder-beta-theme';

function getInitialTheme(): BetaThemeName {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // storage unavailable
  }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

// Single shared store so the provider (forceColorScheme) and the header
// toggle always agree.
let current: BetaThemeName = getInitialTheme();
const listeners = new Set<() => void>();

let transitionTimer: number | undefined;

function applyTheme(value: BetaThemeName) {
  const changed = current !== value;
  current = value;
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // storage unavailable
  }
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    if (
      changed &&
      !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      root.classList.add('beta-theme-transitioning');
      window.clearTimeout(transitionTimer);
      transitionTimer = window.setTimeout(
        () => root.classList.remove('beta-theme-transitioning'),
        300
      );
    }
    root.dataset.betaTheme = value;
  }
  listeners.forEach((l) => l());
}

if (typeof document !== 'undefined') {
  document.documentElement.dataset.betaTheme = current;
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

const getSnapshot = () => current;

export function useBetaTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    theme,
    toggle: () => applyTheme(theme === 'dark' ? 'light' : 'dark'),
  };
}
